import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  orcamentos as api,
  type CotacaoDeFrete,
  type DetalheDoOrcamento,
  type FreteEscolhido,
  type OpcaoDeFrete,
} from '../api/orcamentos'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import { mensagemDeErro } from './mensagemDeErro'

/**
 * O frete do orçamento: cotar, ver as opções, escolher (demanda 053.2).
 *
 * 🔴 **Esta tela é do comercial, e é isso que autoriza a estimativa a
 * aparecer.** O cliente final não chega aqui — na conversa, o assistente com
 * dimensão padrão **não cota** (§4.3 da 053). O comercial sabe ler
 * "estimativa"; o cliente final lê **preço**.
 *
 * 🔴 **O aviso de estimativa aparece UMA vez, acima da lista.** Repeti-lo em
 * cada linha vira ruído e some da leitura — que é o oposto de avisar.
 */
export function FreteDoOrcamento({
  marca,
  orcamento,
}: {
  marca: string
  orcamento: DetalheDoOrcamento
}) {
  const { t } = useTranslation()

  const [cep, setCep] = useState('')
  const [cotando, setCotando] = useState(false)
  const [cotacao, setCotacao] = useState<CotacaoDeFrete | null>(null)
  const [escolhido, setEscolhido] = useState<FreteEscolhido | null>(orcamento.frete)
  const [erro, setErro] = useState<string | null>(null)

  async function cotar(e: FormEvent) {
    e.preventDefault()

    setErro(null)
    setCotando(true)
    setCotacao(null)

    try {
      setCotacao(
        await api.cotarFrete(marca, {
          cepDeDestino: cep,
          tiragem: orcamento.tiragem,
          familia: orcamento.familia,
        }),
      )
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setCotando(false)
    }
  }

  async function escolher(opcao: OpcaoDeFrete) {
    if (!cotacao) return

    setErro(null)

    try {
      setEscolhido(
        await api.escolherFrete(marca, orcamento.numero, {
          transportadora: opcao.transportadora,
          valor: opcao.valor,

          // 🔴 O instante da COTAÇÃO, como o servidor o devolveu. Não é
          // `new Date()`: seria a hora do clique, e a conversa no balcão é
          // sobre quando o valor foi cotado.
          cotadoEm: cotacao.cotadoEm,

          cepDeDestino: cep,
          prazoEmDias: opcao.prazoEmDias,
          servico: opcao.servico,
          estimativa: opcao.estimativa,
        }),
      )
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    }
  }

  return (
    <Cartao titulo={t('frete.titulo')}>
      {escolhido && <Escolhido frete={escolhido} />}

      <form className="usuarios__filtros" onSubmit={cotar}>
        <Campo
          rotulo={t('frete.cep')}
          value={cep}
          onChange={(e) => setCep(e.target.value)}
          ajuda={t('frete.cepAjuda')}
          inputMode="numeric"
        />

        <Botao type="submit" variante="primario" carregando={cotando} disabled={!cep.trim()}>
          {t('frete.cotar')}
        </Botao>
      </form>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {cotando && <Estado tipo="carregando" descricao={t('frete.cotando')} />}

      {cotacao && <Opcoes cotacao={cotacao} aoEscolher={escolher} />}
    </Cartao>
  )
}

function Opcoes({
  cotacao,
  aoEscolher,
}: {
  cotacao: CotacaoDeFrete
  aoEscolher: (o: OpcaoDeFrete) => void
}) {
  const { t } = useTranslation()

  // 🔴 `UI-11`: "não há opção para este destino" ≠ "a transportadora não
  // respondeu". A ação de quem lê é diferente — no primeiro caso muda-se o
  // destino, no segundo tenta-se de novo. O back separa os dois em `motivo` e
  // `naoResponderam`, e a tela não pode fundi-los.
  if (cotacao.opcoes.length === 0)
    return (
      <>
        <Estado
          tipo="vazio"
          titulo={t('frete.semOpcaoTitulo')}
          descricao={cotacao.motivo ?? t('frete.semOpcao')}
        />

        {cotacao.naoResponderam.length > 0 && (
          <Aviso tom="alerta" titulo={t('frete.naoResponderamTitulo')}>
            {t('frete.naoResponderam', { quais: cotacao.naoResponderam.join(', ') })}
          </Aviso>
        )}
      </>
    )

  return (
    <>
      {/* Uma vez, em cima. Ver o resumo do componente. */}
      {cotacao.estimativa && (
        <Aviso tom="alerta" titulo={t('frete.estimativaTitulo')}>
          {t('frete.estimativa')}
        </Aviso>
      )}

      <table className="usuarios__tabela">
        <caption className="usuarios__legenda">{t('frete.legenda')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('frete.transportadora')}</th>
            <th scope="col">{t('frete.servico')}</th>
            <th scope="col">{t('frete.valor')}</th>
            <th scope="col">{t('frete.prazo')}</th>
            <th scope="col">{t('frete.acao')}</th>
          </tr>
        </thead>

        <tbody>
          {cotacao.opcoes.map((o) => (
            <tr key={`${o.transportadora}-${o.servico ?? ''}`}>
              <td>{o.transportadora}</td>
              <td>{o.servico ?? '—'}</td>
              <td className="simulacao__numero">{formatar.moeda(o.valor)}</td>
              <td>{o.prazoEmDias === null ? '—' : t('frete.dias', { dias: o.prazoEmDias })}</td>
              <td>
                <Botao onClick={() => aoEscolher(o)}>{t('frete.escolher')}</Botao>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 🔴 Quem não respondeu APARECE, mesmo com opções na lista: sem isso a
          tela apresenta uma lista parcial como se fosse tudo que existe. */}
      {cotacao.naoResponderam.length > 0 && (
        <Aviso tom="alerta" titulo={t('frete.naoResponderamTitulo')}>
          {t('frete.naoResponderam', { quais: cotacao.naoResponderam.join(', ') })}
        </Aviso>
      )}
    </>
  )
}

/**
 * O que ficou escolhido — com **a data da cotação**, que é a que se explica
 * depois quando o valor não bate com o de hoje.
 */
function Escolhido({ frete }: { frete: FreteEscolhido }) {
  const { t } = useTranslation()

  return (
    <Aviso tom="sucesso" titulo={t('frete.escolhidoTitulo')}>
      <p>
        {t('frete.escolhidoResumo', {
          transportadora: frete.transportadora,
          valor: formatar.moeda(frete.valor),
          prazo: frete.prazoEmDias ?? '—',
        })}
      </p>

      <p>
        {t('frete.escolhidoCotadoEm', { quando: formatar.dataHora(frete.cotadoEm) })}
        {frete.estimativa && ` · ${t('frete.eraEstimativa')}`}
      </p>

      <p className="usuarios__secundario">
        {t('frete.escolhidoPor', {
          quem: frete.escolhidoPor,
          quando: formatar.dataHora(frete.escolhidoEm),
        })}
      </p>
    </Aviso>
  )
}
